import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FolderTree, Pencil, CornerDownRight } from "lucide-react";

function CategoryDialog({ open, onOpenChange, initial, parents, onSaved, defaultParentId }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...initial } : { is_active: true, display_order: 0, parent_id: defaultParentId || null });
  }, [open, initial, defaultParentId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!(form.name || "").trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        parent_id: form.parent_id || null,
        description: form.description || null,
        display_order: Number(form.display_order) || 0,
        is_active: form.is_active !== false,
      };
      if (isEdit) await api.patch(`/product-categories/${initial.id}`, payload);
      else await api.post("/product-categories", payload);
      toast.success(isEdit ? "Category updated" : "Category created");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="category-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">{isEdit ? "Edit category" : "New category"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Name *</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="category-name-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Parent category</Label>
            <Select value={form.parent_id || "__none"} onValueChange={(v) => set("parent_id", v === "__none" ? null : v)}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="category-parent-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="__none">— Top-level category —</SelectItem>
                {parents.filter((p) => p.id !== initial?.id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">Pick a parent to make this a subcategory.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold">Description</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-xs font-semibold">Display order</Label>
              <Input type="number" className="rounded-2xl h-11 mt-1.5 w-28" value={form.display_order ?? 0} onChange={(e) => set("display_order", e.target.value)} data-testid="category-order-input" />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Label className="text-xs font-semibold">Active</Label>
              <Switch checked={form.is_active !== false} onCheckedChange={(v) => set("is_active", v)} data-testid="category-active-switch" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="category-save-button">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const CategoryManager = ({ canManage, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [parentFor, setParentFor] = useState(null);

  const load = () => api.get("/product-categories").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const refresh = () => { load(); onChanged?.(); };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/product-categories/${c.id}`, { name: c.name, is_active: c.is_active === false });
      refresh();
      toast.success(c.is_active === false ? "Category activated" : "Category deactivated");
    } catch (err) { toast.error(fmtError(err)); }
  };

  const tops = rows.filter((c) => !c.parent_id);
  const childrenOf = (id) => rows.filter((c) => c.parent_id === id);

  return (
    <div data-testid="category-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Categories organise the product catalog. Add subcategories under any category.</p>
        {canManage && (
          <Button onClick={() => { setEditing(null); setParentFor(null); setOpen(true); }} className="rounded-2xl shrink-0" data-testid="new-category-button">
            <Plus className="h-4 w-4 mr-1" /> New category
          </Button>
        )}
      </div>
      {tops.length === 0 && (
        <Card className="rounded-3xl border-border/60 shadow-soft"><CardContent className="p-10 text-center text-sm text-muted-foreground">No categories yet. Create your first category to organise products.</CardContent></Card>
      )}
      <div className="space-y-3">
        {tops.map((c) => {
          const kids = childrenOf(c.id);
          const inactive = c.is_active === false;
          return (
            <Card key={c.id} className={`rounded-3xl border-border/60 shadow-soft ${inactive ? "opacity-60" : ""}`} data-testid={`category-card-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center shrink-0"><FolderTree className="h-4 w-4" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-extrabold truncate">{c.name}</p>
                    {c.description && <p className="text-xs text-muted-foreground truncate">{c.description}</p>}
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${inactive ? "bg-stone-100 text-stone-500 border-stone-200" : "bg-primary-50 text-primary-700 border-primary-200"}`}>
                    {inactive ? "Inactive" : "Active"}
                  </Badge>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="rounded-xl h-7 text-xs" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`category-edit-${c.id}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className={`rounded-xl h-7 text-xs ${inactive ? "text-primary-700" : "text-red-600"}`} onClick={() => toggleActive(c)} data-testid={`category-toggle-${c.id}`}>
                        {inactive ? "Activate" : "Deactivate"}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="mt-2 pl-6 space-y-1.5">
                  {kids.map((k) => (
                    <div key={k.id} className="flex items-center gap-2 text-sm" data-testid={`subcategory-row-${k.id}`}>
                      <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className={`flex-1 truncate ${k.is_active === false ? "text-muted-foreground line-through" : ""}`}>{k.name}</span>
                      {canManage && (
                        <>
                          <button type="button" className="text-[10px] underline text-muted-foreground" onClick={() => { setEditing(k); setOpen(true); }}>Edit</button>
                          <button type="button" className={`text-[10px] underline ${k.is_active === false ? "text-primary-700" : "text-red-600"}`} onClick={() => toggleActive(k)}>
                            {k.is_active === false ? "Activate" : "Deactivate"}
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  {canManage && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-primary-700 font-semibold mt-1"
                      onClick={() => { setEditing(null); setParentFor(c.id); setOpen(true); }}
                      data-testid={`add-subcategory-${c.id}`}
                    >
                      <Plus className="h-3 w-3" /> Add subcategory
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <CategoryDialog open={open} onOpenChange={setOpen} initial={editing} parents={tops} onSaved={refresh} defaultParentId={parentFor} />
    </div>
  );
};
